import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import ProgramExplorer from '@/pages/formation/components/ProgramExplorer';
import RegistrationForm from '@/pages/formation/components/RegistrationForm';
import References from '@/pages/formation/components/References';
import { trainingPrograms, trainingSteps } from '@/mocks/formation';
import { company } from '@/mocks/site';
import usePageMeta from '@/hooks/usePageMeta';

const practical = [
  { icon: 'ri-map-pin-2-line', label: 'Lieu', value: 'Atelier SGSCTIC, Rails Logbaba — Douala' },
  { icon: 'ri-time-line', label: 'Horaires', value: '8 h – 16 h, du lundi au vendredi' },
  { icon: 'ri-group-line', label: 'Effectif', value: 'Groupes réduits, 8 à 12 participants' },
  { icon: 'ri-tools-line', label: 'Équipement', value: 'EPI et matériel d’atelier fournis' },
];

export default function Formation() {
  usePageMeta(
    'Formation soudure, chaudronnerie, tuyauterie | SGSCTIC Douala',
    'Cinq parcours de formation en soudure, chaudronnerie, tuyauterie, montage et sécurité à Douala : programme détaillé, sessions en atelier et inscription en ligne.',
  );
  const [activeSlug, setActiveSlug] = useState(trainingPrograms[0].slug);
  const [selectedProgram, setSelectedProgram] = useState('');

  const handleRegister = (programTitle: string) => {
    setSelectedProgram(programTitle);
    document.getElementById('inscription')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <SiteLayout>
      <PageHero
        breadcrumb="Formation"
        label="Centre de formation"
        title="Apprendre le métier, pas seulement la théorie"
        description="Cinq parcours en chaudronnerie, soudure, tuyauterie, montage et sécurité. Théorie courte, atelier long, évaluation sur ouvrage réel : nos stagiaires repartent avec un geste fiable."
        image="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2026-01-06-at-13.50.19.jpeg"
        imageAlt="Stagiaires en formation dans l’atelier SGSCTIC à Douala"
      />

      {/* Programmes */}
      <section id="programmes" className="scroll-mt-24 bg-background-50 py-16 md:py-24">
        <div className="container-wide">
          <Reveal>
            <SectionHeading
              label="Parcours de formation"
              title="Choisissez votre programme"
              description="Chaque parcours détaille ses modules, ses heures d’atelier et ses compétences visées. Sélectionnez un parcours à gauche pour afficher le programme complet."
            />
          </Reveal>

          <div className="mt-10">
            <ProgramExplorer
              activeSlug={activeSlug}
              onChange={setActiveSlug}
              onRegister={handleRegister}
            />
          </div>

          <Reveal delay={140}>
            <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center">
              <p className="flex items-center gap-2 text-sm leading-relaxed text-foreground-700">
                <i className="ri-calendar-event-line text-primary-600" />
                Les prochaines dates de session sont publiées dans le journal de l’atelier.
              </p>
              <Link
                to="/actualites#sessions"
                className="group flex items-center gap-2 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.12em] text-primary-700 transition-colors hover:text-primary-800"
              >
                Voir le calendrier
                <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Admission steps */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="container-wide">
          <Reveal>
            <SectionHeading
              label="Comment s’inscrire"
              title="Quatre étapes, pas de dossier inutile"
              description="De la demande à la session, le parcours d’inscription est simple et rapide."
            />
          </Reveal>

          <div className="mt-10 grid gap-px overflow-hidden rounded-lg bg-background-200 sm:grid-cols-2 lg:grid-cols-4">
            {trainingSteps.map((step, index) => (
              <Reveal key={step.number} delay={index * 90} className="bg-background-50">
                <div className="h-full p-6 md:p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-3xl font-semibold leading-none text-primary-500">
                      {step.number}
                    </span>
                    <i className="ri-arrow-right-up-line text-base text-foreground-300" />
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-semibold uppercase leading-tight text-foreground-950">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Inscription */}
      <section id="inscription" className="scroll-mt-24 bg-background-50 py-16 md:py-24">
        <div className="container-wide">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
            <div>
              <Reveal>
                <SectionHeading
                  label="Inscription"
                  title={
                    <>
                      Une place
                      <br />
                      en atelier
                    </>
                  }
                  description="Les sessions sont planifiées par groupe de niveau. Indiquez le parcours visé : nous vous proposons la prochaine session disponible et les modalités de financement possibles."
                />
              </Reveal>

              <div className="mt-8 space-y-3">
                {practical.map((item, index) => (
                  <Reveal key={item.label} delay={index * 70}>
                    <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100/70 p-4">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                        <i className={`${item.icon} text-base`} />
                      </span>
                      <div className="min-w-0">
                        <p className="label-mono text-foreground-500">{item.label}</p>
                        <p className="mt-1 text-sm leading-snug text-foreground-900">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={320}>
                <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-5">
                  <p className="label-mono text-primary-700">Une question rapide ?</p>
                  <p className="mt-2 text-xs leading-relaxed text-primary-900">
                    Appelez le centre de formation : nous vous orientons vers le parcours adapté à
                    votre niveau.
                  </p>
                  <a
                    href={company.phoneHref}
                    className="mt-3 inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
                  >
                    <i className="ri-phone-line" />
                    {company.phone}
                  </a>
                </div>
              </Reveal>
            </div>

            <Reveal delay={100}>
              <RegistrationForm
                selectedProgram={selectedProgram}
                onProgramChange={setSelectedProgram}
              />
            </Reveal>
          </div>
        </div>
      </section>

      <References />

      <CtaSection
        title="Former une équipe"
        accent="ou rejoindre un parcours ?"
        description="Particulier en reconversion ou entreprise qui veut qualifier ses compagnons : dites-nous votre objectif, nous construisons le programme."
      />
    </SiteLayout>
  );
}